import { Component, Event, EventEmitter, JSX, Method, Prop, State, Watch, h } from '@stencil/core'
import { KeyCode } from '../../enums/keycode.enum'

import { CheckboxEventDetail, CheckboxOption } from '../air-checkbox/air-checkbox.types'

function generateUniqueId(): string {
  return Math.random().toString(36).substr(2, 9)
}

@Component({
  tag: 'air-filter',
  styleUrl: 'air-filter.scss',
  shadow: true,
})
export class AirFilter {
  /** Specifies filter header text */
  @Prop() header = ''

  /** Specifies filter name */
  @Prop({ reflect: true }) name!: string

  /** Specifies filter options */
  @Prop({ mutable: true }) options!: CheckboxOption[]
  @Watch('options')
  watchOptionsChange(newOptions: CheckboxOption[]): void {
    this.initOptions(newOptions)
    this.change.emit(newOptions)
  }

  /** Specifies visible checkboxes limit */
  @Prop() limit = 4

  /** Specifies show more button icon */
  @Prop() iconIndicator = 'chevron-down'

  /** Specifies show more button text */
  @Prop() showMoreButtonText = 'Show more'

  /** Specifies show less button text */
  @Prop() showLessButtonText = 'Show less'

  @State() isFullListShown = false
  @State() allSelected = false

  /** Emits when filter options was changed */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<CheckboxOption[]>

  /** Select all items */
  @Method()
  async selectAll(): Promise<void> {
    this.selectAllItems && this.selectAllItems()
  }

  /** Clear all items */
  @Method()
  async clearAll(): Promise<void> {
    this.clearAllItems && this.clearAllItems()
  }

  private initialOptions?: CheckboxOption[]
  private selectTriggerElement?: HTMLElement

  componentWillLoad(): void {
    this.allSelected = this.checkAllSelected()
  }

  componentDidLoad(): void {
    if (this.options !== undefined) this.initOptions(this.options)
  }

  private initOptions(options: CheckboxOption[]): void {
    this.initialOptions = options
  }

  private handleChange = (event: CustomEvent<CheckboxEventDetail>): void => {
    event.stopPropagation()
    this.initialOptions = this.initialOptions?.map((option) => {
      if (option.name === event.detail.name) {
        option.checked = event.detail.checked
      }
      return option
    })
    this.change?.emit(this.initialOptions)
    this.allSelected = this.checkAllSelected()
  }

  private selectAllItems = (): void => {
    this.options = this.options.map((option) => ({ ...option, checked: true }))
    this.allSelected = true
  }

  private clearAllItems = (): void => {
    this.options = this.options.map((option) => ({ ...option, checked: false }))
    this.allSelected = false
  }

  private handleListOpen = (): void => {
    this.isFullListShown = !this.isFullListShown
  }

  private checkAllSelected(): boolean {
    return this.options.every((option) => option.checked)
  }

  private renderCheckboxes(): HTMLAirCheckboxElement[] {
    const visibleOptions: CheckboxOption[] =
      this.limit > 0 && this.isFullListShown === false
        ? this.options.slice(0, this.limit)
        : this.options
    return visibleOptions?.map((option: CheckboxOption) => {
      const checkboxAttributes = {
        ...option,
        key: generateUniqueId(),
        'onAir-change': this.handleChange,
      }
      return <air-checkbox {...checkboxAttributes}></air-checkbox>
    })
  }

  private renderFooter(): HTMLDivElement {
    const isOverflow = this.options && this.options.length > this.limit
    const text = this.isFullListShown ? this.showLessButtonText : this.showMoreButtonText
    return (
      isOverflow && (
        <div class="footer" onClick={this.handleListOpen} onKeyDown={this.handleKeyDown}>
          <a class="footer-text link" tabindex="0">
            {text}
          </a>
          <air-icon class="indicator" name={this.iconIndicator}></air-icon>
        </div>
      )
    )
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    const keyCodes: string[] = [KeyCode.Space, KeyCode.Enter]
    const { target, code } = event

    if (keyCodes.includes(code) && this.selectTriggerElement === target) {
      event.preventDefault()
      this.selectTriggerElement.click()
    }
  }

  render(): JSX.Element {
    const filterClasses = {
      filter: true,
      'full-list': this.isFullListShown,
    }
    return (
      <div class={filterClasses}>
        <header class="header">
          <div class="header-text">{this.header}</div>
          {this.allSelected ? (
            <a
              ref={(el?: HTMLElement): void => {
                this.selectTriggerElement = el
              }}
              class="link clear-all"
              tabindex="0"
              onClick={this.clearAllItems}
              onKeyDown={this.handleKeyDown}
            >
              Clear All
            </a>
          ) : (
            <a
              ref={(el?: HTMLElement): void => {
                this.selectTriggerElement = el
              }}
              class="link select-all"
              tabindex="0"
              onClick={this.selectAllItems}
              onKeyDown={this.handleKeyDown}
            >
              Select All
            </a>
          )}
        </header>
        <air-divider></air-divider>
        {this.renderCheckboxes()}
        {this.renderFooter()}
      </div>
    )
  }
}
